import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  BookOpen, Award, Clock, Play, ChevronRight, 
  GraduationCap, Loader2, Download, CheckCircle,
  TrendingUp, Target, Sparkles, Share2, Trophy,
  DollarSign, Zap, PartyPopper, Flame
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEARNING_PATHS = [
  {
    id: 'freelancer',
    title: 'AI Freelancer',
    description: 'Start earning money with AI skills',
    icon: DollarSign,
    color: 'from-emerald-500 to-green-600',
    bgLight: 'bg-emerald-50 border-emerald-200',
    textColor: 'text-emerald-600',
    courses: ['chatgpt-mastery', 'prompt-engineering-beginners', 'ai-making-money-online', 'ai-content-creation-system'],
    outcome: 'Land your first AI freelance client',
    milestones: ['AI Foundations', 'Prompt Pro', 'Income Ready', 'Content Machine'],
    shareText: "I just completed the AI Freelancer path on AISkillFlow! Ready to earn with AI skills."
  },
  {
    id: 'creator',
    title: 'AI Content Creator',
    description: 'Build a content empire with AI',
    icon: Sparkles,
    color: 'from-purple-500 to-indigo-600',
    bgLight: 'bg-purple-50 border-purple-200',
    textColor: 'text-purple-600',
    courses: ['chatgpt-mastery', 'midjourney-mastery', 'ai-content-creation-system', 'social-media-growth-ai'],
    outcome: 'Create a week of content in 2 hours',
    milestones: ['AI Foundations', 'Visual Creator', 'Content System', 'Growth Engine'],
    shareText: "I just completed the AI Content Creator path on AISkillFlow! Creating content at 10x speed."
  },
  {
    id: 'builder',
    title: 'AI Business Builder',
    description: 'Automate and scale your business',
    icon: Zap,
    color: 'from-amber-500 to-orange-600',
    bgLight: 'bg-amber-50 border-amber-200',
    textColor: 'text-amber-600',
    courses: ['chatgpt-mastery', 'ai-automation-beginners', 'digital-product-creation-ai', 'ai-research-market-analysis'],
    outcome: 'Build an automated AI-powered business',
    milestones: ['AI Foundations', 'Automation Pro', 'Product Builder', 'Market Expert'],
    shareText: "I just completed the AI Business Builder path on AISkillFlow! Building with AI at scale."
  }
];

const LearningPathCard = ({ path, userCourses, navigate }) => {
  const pathProgress = path.courses.map((slug, idx) => {
    const enrolled = userCourses.find(c => c.slug === slug);
    return {
      slug,
      milestone: path.milestones[idx],
      enrolled: !!enrolled,
      progress: enrolled?.progress_percent || 0,
      complete: enrolled?.is_complete || false,
      title: enrolled?.title || slug.replace(/-/g, ' ')
    };
  });

  const completedCount = pathProgress.filter(c => c.complete).length;
  const totalCount = pathProgress.length;
  const isPathComplete = completedCount === totalCount;
  const overallPercent = totalCount > 0 ? Math.round(pathProgress.reduce((sum, c) => sum + c.progress, 0) / totalCount) : 0;

  const handleShare = () => {
    const text = path.shareText + " https://aiskillflowpro.com";
    if (navigator.share) {
      navigator.share({ text });
    } else {
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
    }
  };

  if (completedCount === 0 && !pathProgress.some(c => c.enrolled)) return null;

  return (
    <Card className={`overflow-hidden ${isPathComplete ? path.bgLight : ''}`} data-testid={`path-${path.id}`}>
      <div className={`bg-gradient-to-r ${path.color} px-5 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2 text-white">
          <path.icon className="w-5 h-5" />
          <h3 className="font-semibold text-sm">{path.title}</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-white/80 text-xs">{completedCount}/{totalCount}</span>
          {isPathComplete && (
            <button onClick={handleShare} className="bg-white/20 hover:bg-white/30 rounded-full p-1.5 transition-colors" title="Share achievement">
              <Share2 className="w-3.5 h-3.5 text-white" />
            </button>
          )}
        </div>
      </div>

      <CardContent className="p-5">
        {isPathComplete && (
          <div className="flex items-center gap-3 mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg" data-testid="path-complete-celebration">
            <Trophy className="w-8 h-8 text-yellow-500 flex-shrink-0" />
            <div>
              <p className="font-semibold text-slate-900 text-sm">Path Complete!</p>
              <p className="text-xs text-slate-600">{path.outcome}</p>
            </div>
            <Button size="sm" variant="outline" className="ml-auto flex-shrink-0" onClick={handleShare}>
              <Share2 className="w-3.5 h-3.5 mr-1" /> Share
            </Button>
          </div>
        )}

        {/* Timeline */}
        <div className="relative">
          {pathProgress.map((step, idx) => (
            <div key={step.slug} className="flex items-start gap-3 relative pb-4 last:pb-0">
              {/* Vertical line */}
              {idx < pathProgress.length - 1 && (
                <div className={`absolute left-[13px] top-7 w-0.5 h-[calc(100%-12px)] ${
                  step.complete ? 'bg-primary' : 'bg-slate-200'
                }`} />
              )}
              {/* Circle indicator */}
              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${
                step.complete
                  ? 'bg-primary text-white'
                  : step.enrolled
                    ? 'bg-primary/20 text-primary border-2 border-primary'
                    : 'bg-slate-100 text-slate-400 border border-slate-200'
              }`}>
                {step.complete ? <CheckCircle className="w-4 h-4" /> : <span className="text-xs font-bold">{idx + 1}</span>}
              </div>
              {/* Content */}
              <div className="flex-1 min-w-0 pt-0.5">
                <div className="flex items-center gap-2">
                  <p className={`text-sm font-medium truncate ${step.complete ? 'text-slate-900' : step.enrolled ? 'text-slate-700' : 'text-slate-400'}`}>
                    {step.milestone}
                  </p>
                  {step.complete && <Badge className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0">Done</Badge>}
                  {step.enrolled && !step.complete && <Badge variant="outline" className="text-[10px] px-1.5 py-0">{step.progress}%</Badge>}
                </div>
                <button
                  onClick={() => navigate(`/academy/course/${step.slug}`)}
                  className="text-xs text-slate-500 hover:text-primary transition-colors truncate block"
                >
                  {step.title}
                </button>
              </div>
            </div>
          ))}
        </div>

        {!isPathComplete && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>{overallPercent}% overall progress</span>
              <span className={path.textColor}>{path.outcome}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const AcademyDashboard = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiInsight, setAiInsight] = useState('');
  const [streak, setStreak] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login?redirect=/academy/dashboard');
      return;
    }
    fetchDashboard();
  }, [user, token]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/academy/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
      // Fetch AI insight in background (non-blocking)
      axios.get(`${API}/academy/dashboard/insight`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => setAiInsight(res.data?.insight || '')).catch(() => {});
      // Fetch streak data
      axios.get(`${API}/academy/streak`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => setStreak(res.data)).catch(() => {});
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  const hasMembership = dashboard?.membership?.has_access;
  const stats = dashboard?.stats || {};
  const courses = dashboard?.courses || [];
  const certificates = dashboard?.certificates || [];
  const continueCourse = dashboard?.continue_course;
  const recommendations = dashboard?.recommendations || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="academy-dashboard">
      <Navbar />

      {/* Header */}
      <div className="pt-16 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h1 className="font-heading text-3xl font-bold text-white mb-2">
                Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!
              </h1>
              <p className="text-slate-400">
                {hasMembership 
                  ? 'Continue your AI learning journey'
                  : 'Upgrade to unlock all courses'}
              </p>
              {aiInsight && (
                <div className="mt-3 flex items-start gap-2 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2.5 max-w-xl" data-testid="ai-insight">
                  <Sparkles className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-sm text-slate-300 leading-relaxed">{aiInsight}</p>
                </div>
              )}
            </div>
            
            {hasMembership ? (
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-sm py-2 px-4">
                <Sparkles className="w-4 h-4 mr-2" />
                Active Member
              </Badge>
            ) : (
              <Button onClick={() => navigate('/academy/membership')}>
                Upgrade to Membership
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {[
            { label: 'Enrolled Courses', value: stats.enrolled_courses || 0, icon: BookOpen },
            { label: 'Completed', value: stats.completed_courses || 0, icon: CheckCircle },
            { label: 'Lessons Done', value: stats.total_lessons_completed || 0, icon: Target },
            { label: 'Certificates', value: stats.certificates_earned || 0, icon: Award },
            { label: 'Day Streak', value: streak?.current_streak || 0, icon: Flame, highlight: (streak?.current_streak || 0) >= 3 }
          ].map((stat, idx) => (
            <Card key={idx} className={stat.highlight ? 'border-orange-200 bg-orange-50/50' : ''}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.highlight ? 'bg-orange-100' : 'bg-primary/10'}`}>
                    <stat.icon className={`w-5 h-5 ${stat.highlight ? 'text-orange-500' : 'text-primary'}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                    <p className="text-xs text-slate-500">{stat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Continue Learning */}
        {continueCourse && (
          <Card className="mb-8 bg-gradient-to-r from-primary/5 to-purple-50 border-primary/20">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center gap-6">
                <img 
                  src={continueCourse.thumbnail_url} 
                  alt={continueCourse.title}
                  className="w-full md:w-48 h-32 object-cover rounded-lg"
                />
                <div className="flex-1">
                  <Badge variant="outline" className="mb-2">Continue Learning</Badge>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                    {continueCourse.title}
                  </h3>
                  <div className="flex items-center gap-4 mb-3">
                    <Progress value={continueCourse.progress_percent} className="flex-1 h-2" />
                    <span className="text-sm font-medium text-primary">
                      {continueCourse.progress_percent}%
                    </span>
                  </div>
                  <p className="text-sm text-slate-600">
                    {continueCourse.completed_lessons_count} of {continueCourse.total_lessons} lessons completed
                  </p>
                </div>
                <Button 
                  size="lg"
                  onClick={() => navigate(`/academy/course/${continueCourse.slug}`)}
                >
                  <Play className="w-5 h-5 mr-2" />
                  Continue
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* My Courses */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-semibold text-slate-900">My Courses</h2>
              <Button variant="ghost" size="sm" onClick={() => navigate('/academy/courses')}>
                Browse All
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            {courses.length === 0 ? (
              <Card className="p-8 text-center">
                <GraduationCap className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">No courses started yet</h3>
                <p className="text-slate-600 mb-4">
                  Browse our courses and start learning today!
                </p>
                <Button onClick={() => navigate('/academy/courses')}>
                  Browse Courses
                </Button>
              </Card>
            ) : (
              <div className="space-y-4">
                {courses.map((course) => (
                  <Card key={course.id} className="overflow-hidden hover:shadow-md transition-shadow">
                    <div className="flex">
                      <img 
                        src={course.thumbnail_url} 
                        alt={course.title}
                        className="w-32 h-24 object-cover"
                      />
                      <CardContent className="p-4 flex-1">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-slate-900 mb-1">
                              {course.title}
                            </h3>
                            <div className="flex items-center gap-2 mb-2">
                              <Progress value={course.progress_percent} className="w-24 h-2" />
                              <span className="text-xs text-slate-500">
                                {course.progress_percent}%
                              </span>
                            </div>
                            <p className="text-xs text-slate-500">
                              {course.completed_lessons_count}/{course.total_lessons} lessons
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            {course.is_complete ? (
                              <Badge className="bg-green-100 text-green-800">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Complete
                              </Badge>
                            ) : (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => navigate(`/academy/course/${course.slug}`)}
                              >
                                Continue
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Certificates */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Award className="w-5 h-5 text-primary" />
                  Certificates
                </CardTitle>
              </CardHeader>
              <CardContent>
                {certificates.length === 0 ? (
                  <div className="text-center py-4">
                    <Award className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">
                      Complete courses to earn certificates
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {certificates.map((cert) => (
                      <div 
                        key={cert.id}
                        className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
                      >
                        <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                          <Award className="w-5 h-5 text-yellow-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {cert.course_title}
                          </p>
                          <p className="text-xs text-slate-500">
                            Earned {new Date(cert.issued_at).toLocaleDateString()}
                          </p>
                        </div>
                        <a
                          href={`${API}/academy/certificate/${cert.id}/download`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button variant="ghost" size="icon" data-testid={`download-cert-${cert.id}`}>
                            <Download className="w-4 h-4" />
                          </Button>
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Membership Status */}
            <Card className={hasMembership ? 'bg-green-50 border-green-200' : ''}>
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    hasMembership ? 'bg-green-100' : 'bg-slate-100'
                  }`}>
                    <Sparkles className={`w-5 h-5 ${hasMembership ? 'text-green-600' : 'text-slate-400'}`} />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">
                      {hasMembership ? 'Active Membership' : 'No Membership'}
                    </p>
                    {hasMembership && dashboard.membership.expires_at && (
                      <p className="text-xs text-slate-500">
                        Expires: {new Date(dashboard.membership.expires_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
                {!hasMembership && (
                  <Button 
                    className="w-full" 
                    onClick={() => navigate('/academy/membership')}
                  >
                    Upgrade Now
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Quick Links */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Quick Links</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Link to="/academy/courses">
                  <Button variant="ghost" className="w-full justify-start">
                    <BookOpen className="w-4 h-4 mr-2" />
                    All Courses
                  </Button>
                </Link>
                <Link to="/academy/start-here">
                  <Button variant="ghost" className="w-full justify-start">
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Start Here Guide
                  </Button>
                </Link>
                <Link to="/academy/coach">
                  <Button variant="ghost" className="w-full justify-start">
                    <Sparkles className="w-4 h-4 mr-2" />
                    AI Coach
                  </Button>
                </Link>
                <Link to="/academy">
                  <Button variant="ghost" className="w-full justify-start">
                    <GraduationCap className="w-4 h-4 mr-2" />
                    Academy Home
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Learning Path Progress */}
        {courses.length > 0 && (
          <div className="mt-8" data-testid="learning-path-progress">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">Learning Path Progress</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {LEARNING_PATHS.map((path) => (
                <LearningPathCard key={path.id} path={path} userCourses={courses} navigate={navigate} />
              ))}
            </div>
            {LEARNING_PATHS.every(p => !p.courses.some(s => courses.find(c => c.slug === s))) && (
              <Card className="p-6 text-center mt-4">
                <TrendingUp className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <h3 className="font-semibold text-slate-900 mb-1">Start a Learning Path</h3>
                <p className="text-sm text-slate-600 mb-3">Choose a guided path on the Academy page to get started</p>
                <Button variant="outline" onClick={() => navigate('/academy')}>
                  View Learning Paths
                </Button>
              </Card>
            )}
          </div>
        )}

        {/* Recommendations Section */}
        {recommendations.length > 0 && (
          <div className="mt-8" data-testid="recommendations-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-semibold text-slate-900">Recommended for You</h2>
              <Button variant="ghost" size="sm" onClick={() => navigate('/academy/courses')}>
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {recommendations.map((rec) => (
                <Card key={rec.id} className="group overflow-hidden hover:shadow-lg transition-all cursor-pointer"
                  onClick={() => navigate(`/academy/course/${rec.slug}`)} data-testid={`rec-${rec.slug}`}>
                  <div className="aspect-video relative overflow-hidden">
                    <img src={rec.thumbnail_url} alt={rec.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-2 left-2">
                      <Badge className="bg-white/90 text-slate-800 text-xs">{rec.level}</Badge>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <p className="text-xs text-primary font-medium mb-1">{rec.recommendation_reason}</p>
                    <h3 className="font-semibold text-slate-900 mb-1 line-clamp-1 group-hover:text-primary transition-colors">
                      {rec.title}
                    </h3>
                    <p className="text-xs text-slate-500">{rec.total_lessons} lessons</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcademyDashboard;
